import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../../context/LangContext";
import "./SkillsSection.css";

const nodes = [
  { id: "react", label: "React", group: "frontend", x: 20, y: 18 },
  { id: "nextjs", label: "Next.js", group: "frontend", x: 35, y: 8 },
  { id: "typescript", label: "TypeScript", group: "frontend", x: 8, y: 38 },
  { id: "tailwind", label: "Tailwind", group: "frontend", x: 24, y: 40 },
  { id: "figma", label: "Figma", group: "frontend", x: 6, y: 58 },
  { id: "csharp", label: "C#", group: "backend", x: 55, y: 12 },
  { id: "dotnet", label: ".NET", group: "backend", x: 70, y: 22 },
  { id: "java", label: "Java", group: "backend", x: 82, y: 10 },
  { id: "spring", label: "Spring Boot", group: "backend", x: 88, y: 28 },
  { id: "nodejs", label: "Node.js", group: "backend", x: 60, y: 38 },
  { id: "jwt", label: "JWT/Auth", group: "backend", x: 78, y: 44 },
  { id: "postgres", label: "PostgreSQL", group: "db", x: 42, y: 60 },
  { id: "mysql", label: "MySQL", group: "db", x: 60, y: 68 },
  { id: "oracle", label: "Oracle", group: "db", x: 76, y: 60 },
  { id: "git", label: "Git", group: "tools", x: 18, y: 72 },
  { id: "docker", label: "Docker", group: "tools", x: 34, y: 82 },
  { id: "azure", label: "Azure", group: "tools", x: 50, y: 88 },
  { id: "scrum", label: "Scrum", group: "tools", x: 66, y: 84 },
  { id: "junit", label: "JUnit/NUnit", group: "tools", x: 84, y: 74 },
];

const edges = [
  ["react", "nextjs"],
  ["react", "typescript"],
  ["react", "tailwind"],
  ["typescript", "tailwind"],
  ["csharp", "dotnet"],
  ["java", "spring"],
  ["dotnet", "jwt"],
  ["nodejs", "jwt"],
  ["csharp", "postgres"],
  ["nodejs", "mysql"],
  ["spring", "mysql"],
  ["dotnet", "oracle"],
  ["react", "nodejs"],
  ["git", "docker"],
  ["docker", "azure"],
  ["scrum", "git"],
  ["csharp", "junit"],
  ["java", "junit"],
  ["postgres", "docker"],
  ["nextjs", "typescript"],
  ["react", "figma"],
  ["tailwind", "figma"],
];

const groupColors = {
  frontend: "#10b981",
  backend: "#6366f1",
  db: "#f59e0b",
  tools: "#64748b",
};

export default function SkillsSection() {
  const { t } = useLang();
  const s = t.skills;
  const [active, setActive] = useState(null);

  const activeNode = nodes.find((n) => n.id === active);
  const connectedIds = active
    ? new Set(
        edges
          .filter(([a, b]) => a === active || b === active)
          .map(([a, b]) => (a === active ? b : a)),
      )
    : new Set();

  const nodeOpacity = (id) =>
    !active ? 1 : id === active || connectedIds.has(id) ? 1 : 0.15;
  const edgeOpacity = (a, b) =>
    !active ? 0.18 : a === active || b === active ? 0.85 : 0.04;
  const edgeStroke = (a, b) =>
    !active
      ? "#10b98144"
      : a === active || b === active
        ? (groupColors[activeNode?.group] ?? "#10b981")
        : "#10b98110";

  return (
    <section className="skills-section" id="skills">
      <div className="skills-container">
        <motion.div
          className="skills-content"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9 }}
        >
          <span className="skills-code">{s.tag}</span>
          <h1 className="skills-title">
            {s.title} <span className="skills-highlight">.</span>
          </h1>
          <p className="skills-desc">{s.desc}</p>

          <div className="skills-legend">
            {Object.entries(groupColors).map(([g, color]) => (
              <div className="skills-legend-item" key={g}>
                <span
                  className="skills-legend-dot"
                  style={{ background: color }}
                />
                <span className="skills-legend-label">{s.groups[g]}</span>
              </div>
            ))}
          </div>

          <div className="skills-hint" style={{ opacity: active ? 1 : 0 }}>
            {active && (
              <>
                <span
                  className="skills-hint-name"
                  style={{ color: groupColors[activeNode?.group] }}
                >
                  {activeNode?.label}
                </span>
                {connectedIds.size > 0 && (
                  <span className="skills-hint-connections">
                    {" "}
                    {s.hintPrefix}{" "}
                    {[...connectedIds]
                      .map((id) => nodes.find((n) => n.id === id)?.label)
                      .join(", ")}
                  </span>
                )}
              </>
            )}
          </div>
        </motion.div>

        <motion.div
          className="skills-graph-wrap"
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, delay: 0.15 }}
        >
          <svg
            className="skills-graph"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
          >
            {edges.map(([a, b], i) => {
              const na = nodes.find((n) => n.id === a);
              const nb = nodes.find((n) => n.id === b);
              if (!na || !nb) return null;
              return (
                <line
                  key={i}
                  x1={na.x}
                  y1={na.y}
                  x2={nb.x}
                  y2={nb.y}
                  stroke={edgeStroke(a, b)}
                  strokeWidth={
                    active && (a === active || b === active) ? 0.55 : 0.3
                  }
                  strokeOpacity={edgeOpacity(a, b)}
                  style={{
                    transition:
                      "stroke-opacity 0.25s,stroke 0.25s,stroke-width 0.25s",
                  }}
                />
              );
            })}
            {nodes.map((node) => (
              <g
                key={node.id}
                style={{
                  opacity: nodeOpacity(node.id),
                  transition: "opacity 0.25s",
                  cursor: "pointer",
                }}
                onMouseEnter={() => setActive(node.id)}
                onMouseLeave={() => setActive(null)}
              >
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={active === node.id ? 3.2 : 2.2}
                  fill={groupColors[node.group]}
                  style={{ transition: "r 0.2s" }}
                  opacity={0.9}
                />
                <text
                  x={node.x}
                  y={node.y - 3.3}
                  textAnchor="middle"
                  fontSize="2.8"
                  fill={active === node.id ? groupColors[node.group] : "#ccc"}
                  fontFamily="'Fira Mono',monospace"
                  fontWeight={active === node.id ? "700" : "400"}
                  style={{ transition: "fill 0.2s" }}
                  pointerEvents="none"
                >
                  {node.label}
                </text>
              </g>
            ))}
          </svg>
        </motion.div>
      </div>
    </section>
  );
}
